import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import ChatChannelListContext from '../../../../chat/context/ChatChannelListContext/ChatChannelListContext';
import ChatChannel from '../../../../chat/Model/ChatChannel';
import { AVIDA_SYSTEM_USER_ID } from '../../../../chat/utils/reactions';
import LoggedInUserContext from '../../../../context/LoggedInUserContext';
import ExternalCoachContext from '../../../context/ExternalCoachContext/ExternalCoachContext';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import LoadingBadge from '../../../../components/LoadingBadge';
import useToast from '../../../hooks/useToast';
import useToolsNavigation from '../../../hooks/useToolsNavigation';
import CoachHistory, { HistoryEventType } from '../../../Model/CoachHistory';
import AddSocialHandlesModal from '../AddSocialHandlesModal';
import ISACQuestionnaireModal from '../ISACQuestionnaireModal';
import ContentURLModal from '../ContentURLModal';

import {
  StyledButton,
  StyledListElement,
  StyledLabel,
  StyledStatus,
  StyledDescription,
  StyledTime,
  StyledAccessTimeIcon,
  TimeRow,
  StyledCol,
  StyledButtonContainer,
} from './styles';

import texts from './texts.json';

const CoachOnboardingTask = ({
  path,
  buttonText,
  label,
  description,
  timeInMinutes,
  onClose,
  historyItem,
  answers,
}) => {
  const [isSocialHandlesModalOpen, setIsSocialHandlesModalOpen] = useState(false);
  const [isAcQuestionnaireModalOpen, setIsAcQuestionnaireModalOpen] = useState(false);
  const [isContentURLsModalOpen, setIsContentURLsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const componentMountedRef = useComponentMounted();

  const {
    userId: loggedInUserId,
    userDoc: {
      name: loggedInUserName,
    },
  } = useContext(LoggedInUserContext);

  const isCoachLeadView = !!answers;

  const externalCoachContext = useContext(ExternalCoachContext);
  const coachId = !isCoachLeadView ? externalCoachContext?.coachDoc?.id : undefined;

  const { navigateTo } = useToolsNavigation();
  const { showToast } = useToast();
  const isComponentMounted = useComponentMounted();
  const { setActiveUserId } = useContext(ChatChannelListContext);

  /*
   * The coach's Avida coaching channel is their channel with the Avida system user, so making that user active
   * opens it on the Chat page, the way a client's chat button opens the client's. Only when the coach has one:
   * a coach not migrated yet has none, and the chat would then look it up, fail and report it as not found.
   * Without it (or if the lookup fails) the Chat page opens with nothing selected.
   */
  const openAvidaChat = async () => {
    try {
      const avidaChannel = await ChatChannel.getUserCoachChannel(AVIDA_SYSTEM_USER_ID, coachId);
      if (avidaChannel) {
        setActiveUserId(AVIDA_SYSTEM_USER_ID);
      }
    } catch {
      // Open the Chat page anyway, with no channel selected
    }
    navigateTo(path);
    onClose();
  };

  const renderAction = () => {
    if (historyItem === HistoryEventType.FIRST_CHAT_MESSAGE_SENT) {
      openAvidaChat();
    } else if (historyItem === HistoryEventType.SOCIAL_HANDLES_ADDED) {
      setIsSocialHandlesModalOpen(true);
    } else if (historyItem === HistoryEventType.IS_AC_QUESTIONNAIRE_SUBMITTED) {
      setIsAcQuestionnaireModalOpen(true);
    } else if (historyItem === HistoryEventType.CONTENT_URLS_SUBMITTED) {
      setIsContentURLsModalOpen(true);
    } else if (historyItem === HistoryEventType.COACH_ONBOARDING_FORM_COMPLETED) {
      window.open(path, '_blank');
    } else {
      navigateTo(path);
      onClose();
    }
  };

  const showMarkAsCompleteButton = useMemo(() => {
    if (historyItem && historyItem === HistoryEventType.GOOGLE_CALENDAR_CREATED) {
      return true;
    }
    if (historyItem && historyItem === HistoryEventType.COACH_ONBOARDING_FORM_COMPLETED && !isCoachLeadView) {
      return true;
    }
    return false;
  }, [historyItem, isCoachLeadView]);

  const onMarkAsComplete = useCallback(async () => {
    setIsLoading(true);
    try {
      await CoachHistory.addCoachHistoryItem(
        {
          type: historyItem,
          coachId,
          actionedBy: loggedInUserId,
          actionedByUserName: loggedInUserName,
        },
      );
      if (componentMountedRef.current) {
        showToast(historyItem === HistoryEventType.GOOGLE_CALENDAR_CREATED
          ? texts.googleCalendarCreatedSuccessfully
          : texts.coachOnboardingFormCompletedSuccessfully);
      }
    } catch (error) {
      if (isComponentMounted.current) {
        showToast((format(historyItem === HistoryEventType.GOOGLE_CALENDAR_CREATED
          ? texts.googleCalendarCreatedError
          : texts.coachOnboardingFormCompletedError, { error: error.message })), { error: true });
      }
    }
    setIsLoading(false);
  }, [
    historyItem,
    coachId,
    loggedInUserId,
    loggedInUserName,
    componentMountedRef,
    showToast,
    isComponentMounted,
  ]);

  return (
    <>
      <StyledListElement key={label}>
        <StyledCol>
          <div>
            <StyledLabel>
              {label}
            </StyledLabel>
            {!isCoachLeadView && (
              <StyledStatus>
                {texts.pending}
              </StyledStatus>
            )}
          </div>
          <StyledDescription>
            {description}
          </StyledDescription>
          {!isCoachLeadView && (
          <TimeRow>
            <StyledTime>
              <StyledAccessTimeIcon />
              {format(texts.time, { timeInMinutes })}
            </StyledTime>
          </TimeRow>
          )}
        </StyledCol>
        <StyledButtonContainer>
          {historyItem !== HistoryEventType.GOOGLE_CALENDAR_CREATED && (
          <div>
            <StyledButton onClick={renderAction}>
              {buttonText}
            </StyledButton>
          </div>
          )}
          {showMarkAsCompleteButton && (
          <div>
            <StyledButton onClick={onMarkAsComplete}>
              {isLoading ? <LoadingBadge /> : texts.markAsComplete}
            </StyledButton>
          </div>
          )}
        </StyledButtonContainer>
      </StyledListElement>

      {isSocialHandlesModalOpen && (
        <AddSocialHandlesModal
          coachId={coachId}
          loggedInUserId={loggedInUserId}
          loggedInUserName={loggedInUserName}
          onClose={() => {
            setIsSocialHandlesModalOpen(false);
          }}
        />
      )}

      {isAcQuestionnaireModalOpen && (
        <ISACQuestionnaireModal
          coachId={coachId}
          loggedInUserId={loggedInUserId}
          loggedInUserName={loggedInUserName}
          coachAnswers={answers}
          onClose={() => {
            setIsAcQuestionnaireModalOpen(false);
          }}
        />
      )}
      {isContentURLsModalOpen && (
        <ContentURLModal
          coachId={coachId}
          loggedInUserId={loggedInUserId}
          loggedInUserName={loggedInUserName}
          coachAnswers={answers}
          onClose={() => {
            setIsContentURLsModalOpen(false);
          }}
        />
      )}
    </>
  );
};

CoachOnboardingTask.propTypes = {
  path: PropTypes.string,
  buttonText: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string,
  timeInMinutes: PropTypes.string,
  onClose: PropTypes.func,
  historyItem: PropTypes.string,
  answers: PropTypes.object,
};

CoachOnboardingTask.defaultProps = {
  path: '',
  buttonText: '',
  label: '',
  description: '',
  timeInMinutes: '0',
  onClose: () => { },
  historyItem: '',
  answers: null,
};

export default CoachOnboardingTask;
